import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useCallback, useMemo, useState} from 'react'
import {sortFindingsBySeverity} from './entity-helpers'
import type {Finding} from './types'

const ignoreFindingIds = async (basePath: string, ids: string[]): Promise<Response> => {
  const response = await verifiedFetchJSON(`${basePath}/code_review_findings/ignore`, {
    method: 'POST',
    body: {entity_ids: ids},
  })
  if (!response.ok) {
    throw new Error(`Failed to ignore findings (status ${response.status} ${response.statusText})`)
  }
  return response
}

const unignoreFindingIds = async (basePath: string, ids: string[]): Promise<Response> => {
  const response = await verifiedFetchJSON(`${basePath}/code_review_findings/ignore`, {
    method: 'DELETE',
    body: {entity_ids: ids},
  })
  if (!response.ok) {
    throw new Error(`Failed to unignore findings (status ${response.status} ${response.statusText})`)
  }
  return response
}

export function useIgnoreFindings(findings: Finding[], pullRequestBasePath: string, ignoredEntityIds: string[]) {
  // Track only local optimistic overrides (true = dismissed, false = reopened).
  // The effective ignored set is derived from server state (`ignoredEntityIds`) + overrides.
  const [localOverrides, setLocalOverrides] = useState<Map<string, boolean>>(
    () => new Map(ignoredEntityIds.map(id => [id, true])),
  )

  const handleDismiss = useCallback(
    async (findingsToDismiss: Finding[]) => {
      const ids = findingsToDismiss.map(f => f.id)
      setLocalOverrides(prev => {
        const next = new Map(prev)
        for (const id of ids) next.set(id, true)
        return next
      })
      try {
        await ignoreFindingIds(pullRequestBasePath, ids)
      } catch {
        // Rollback optimistic state on failure
        setLocalOverrides(prev => {
          const next = new Map(prev)
          for (const id of ids) next.delete(id)
          return next
        })
      }
    },
    [pullRequestBasePath],
  )

  const handleReopen = useCallback(
    async (findingsToReopen: Finding[]) => {
      const ids = findingsToReopen.map(f => f.id)
      setLocalOverrides(prev => {
        const next = new Map(prev)
        for (const id of ids) next.set(id, false)
        return next
      })
      try {
        await unignoreFindingIds(pullRequestBasePath, ids)
      } catch {
        // Rollback optimistic state on failure
        setLocalOverrides(prev => {
          const next = new Map(prev)
          for (const id of ids) next.delete(id)
          return next
        })
      }
    },
    [pullRequestBasePath],
  )

  const ignoredIds = useMemo(() => {
    const ids = new Set(ignoredEntityIds)
    for (const [id, isIgnored] of localOverrides) {
      if (isIgnored) ids.add(id)
      else ids.delete(id)
    }
    return ids
  }, [ignoredEntityIds, localOverrides])

  const openFindings = useMemo(
    () => sortFindingsBySeverity(findings.filter(f => !ignoredIds.has(f.id))),
    [findings, ignoredIds],
  )

  const ignoredFindings = useMemo(
    () => sortFindingsBySeverity(findings.filter(f => ignoredIds.has(f.id))),
    [findings, ignoredIds],
  )

  return {openFindings, ignoredFindings, handleDismiss, handleReopen}
}
